// Calculate the number of through posts for a given rail length in millimeters.
// This uses the formula: through_posts = max(0, floor(length_mm / 300 - 1)).
const throughPostsForLength = (lengthMm) =>
  Math.max(0, Math.trunc(lengthMm / 300 - 1));

// API will reject anything longer than this
const MAX_RAIL_LENGTH_MM = 2699;

// Fallback when no finish tile is checked. Must stay in sync with the
// default_finish passed to v2-product-finishes-tile-grilles in the section,
// and must be a finish the API accepts — an unknown code returns HTTP 500.
const DEFAULT_FINISH = "SBUL";

// Capture enquiry errors
const ENQUIRY_ERRORS = ["RAIL_LENGTH_ENQUIRY", "THROUGH_POSTS_ENQUIRY"];

// Map an API error_name to the form field its message belongs under. Errors not
// listed here (e.g. unsupported finish, pricing 404s) have no natural field and
// fall back to the global info-box. "returns" is a virtual key that resolves to
// both visible return sub-fields, since the API does not say which side failed.
const FIELD_ERROR_MAP = {
  INVALID_RAIL_LENGTH: "rail_length",
  RAIL_LENGTH_ENQUIRY: "rail_length",
  THROUGH_POSTS_ENQUIRY: "through_posts",
  TWO_END_POSTS_WITH_WALL_BRACKETS: "termination",
  INVALID_RETURN_LENGTH: "returns",
};

// When the user edits an input, forget the remembered errors that input could
// plausibly fix, so the field gets a fresh chance to re-validate. Keys are input
// `name`s; values are the remembered field-error keys to drop. "finish" is not
// listed here because its error is global, not per-field — see
// forgetErrorsForInput(). rail_length also drops "through_posts" because through
// posts are derived from rail length; the return inputs drop the virtual
// "returns" key because the API does not say which side failed.
// Prefixed to every inline field message. Inline rather than a snippet because
// field errors are built in JS, not Liquid.
const ERROR_ICON = `<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm-.75 3.5h1.5v5h-1.5v-5Zm0 6.25h1.5v1.5h-1.5v-1.5Z"/></svg>`;

const FORGET_ON_EDIT = {
  rail_length: ["rail_length", "through_posts"],
  termination: ["termination"],
  returns: ["returns", "left_return_length", "right_return_length"],
  left_return_length: ["returns", "left_return_length"],
  right_return_length: ["returns", "right_return_length"],
};

class GalleryRailBuilderForm extends HTMLElement {
  constructor() {
    super();

    // Everything a price request produces, filled in as the user configures
    this.product = {
      sku: "",
      price: null,
      rrp: null,
    };

    // Per-store settings injected from Liquid (currency, unit, customer, VAT, etc)
    this.config = {};

    // The Base API URL we'll pass the params to
    this.API_URL =
      "https://armacgrilleapi-282561298653.europe-west1.run.app/api/gallery_rail";

    this.hasValidPrice = false;

    // Remembered error state. The pricing API returns only ONE error per
    // response, so each field's error must persist here until the user edits
    // that field (or a successful price clears everything) — otherwise a fresh
    // response for one field would wipe the errors already shown on the others.
    // fieldErrors maps a field key (see FIELD_ERROR_MAP / the return field
    // names) to its message; globalError holds the info-box message, if any.
    this.fieldErrors = new Map();
    this.globalError = null;

    // Every resolve() gets an id
    this.lastRequestId = 0;

    // Enquiry reason
    this.lastEnquiryReason = null;

    // Image base URL and params for the preview image
    this.IMAGE_BASE_URL = `https://${window.Shopify?.cdnHost}/shop/files`;
    this.IMAGE_PARAMS = "width=1024&height=1024&quality=90";
  }

  /* -------------------------------------------------------------------------- */
  /*                Init once the element is connected to the DOM               */
  /* -------------------------------------------------------------------------- */

  connectedCallback() {
    this.parseConfig();
    console.log("[gallery-rail] connected with config:", this.config);

    // Cache the elements we need to write to
    this.skuEl = this.querySelector(".gallery-rail__form__sku");
    // Rendered by v2-product-price snippet. The RRP elements only
    // exist for B2B customers, so their presence is the B2B check.
    this.priceEls = this.querySelectorAll(".price-value");
    this.rrpEls = this.querySelectorAll(".price-value-rrp");
    this.errorEl = this.querySelector(
      ".info-box.info-box__gallery-rail-error p",
    );
    this.throughPostsEl = this.querySelector("[data-through-posts]");

    // Cache the enquiry modal and button
    this.enquiryForm = document.querySelector("enquiry-form");
    this.enquireButton = this.querySelector('button[name="enquire"]');
    if (this.enquireButton) {
      this.enquireButton.addEventListener("click", () => this.openEnquiry());
    }

    // Cache the add-to-cart button
    this.addToCartButton = this.querySelector('button[name="add-to-cart"]');
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener("click", () =>
        this.handleAddToCart(),
      );
    }

    // Cache the gallery layers container and placeholder
    const wrapper = this.closest(".gallery-rail__wrapper");
    this.layersContainer = wrapper?.querySelector("[data-layers-container]");
    this.placeholderEl = wrapper?.querySelector(
      ".gallery-rail__gallery__placeholder",
    );
    this.loadingEl = wrapper?.querySelector("[data-gallery-loading]");

    this.watchForChanges();
    this.updateReturnFields();
    this.updateTerminationOptions();

    // The country lookup is async, so a restriction can land after this
    // component has already priced (or failed to). Re-resolve when it does, so
    // the pricing UI reflects it.
    document.addEventListener("geo:restricted", () => this.resolve());

    this.resolve();
  }

  /* -------------------------------------------------------------------------- */
  /*                                Form reading                                */
  /* -------------------------------------------------------------------------- */

  /**
   * Parse the JSON config from the hidden #gallery-rail-config element
   * and store it in this.config
   */

  parseConfig() {
    const el = document.getElementById("gallery-rail-config");
    try {
      this.config = JSON.parse(el?.textContent || "{}");
    } catch (err) {
      console.error("[gallery-rail] could not parse #gallery-rail-config", err);
      this.config = {};
    }
  }

  /**
   * Get the value of a radio button group by name.
   * If no button is checked, return null.
   * @param {string} name - The name of the radio button group
   * @returns {string|null} The value of the checked radio button,
   *                        or null if none are checked
   */

  radio(name) {
    // If no radio button is checked, return null instead of undefined
    const checked = this.querySelector(`input[name="${name}"]:checked`);
    return checked ? checked.value : null;
  }

  /**
   * Get the value of a text input by name.
   * If no input is found, return an empty string.
   * @param {string} name - The name of the text input
   * @returns {string} The value of the text input,
   *                   or an empty string if not found
   */

  value(name) {
    // If no field is found, return an empty string instead of undefined
    const field = this.querySelector(`input[name="${name}"]`);
    return field ? field.value.trim() : "";
  }

  /**
   * Get the quantity to add to the cart. The selector is Alpine-backed and can
   * be cleared by hand, so anything that is not a positive whole number falls
   * back to 1.
   * @returns {number} The quantity, at least 1
   */

  quantity() {
    const qty = parseInt(this.value("quantity"), 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /**
   * Parse a measurement the user typed into a number.
   *
   * With `fraction_input` on, accepts a mixed number ("2 1/2"), a bare fraction
   * ("1/2") or a plain decimal ("2.5"), and returns NaN for anything else so
   * callers can tell a typo from a real value. Mirrors parseDimensionValue() in
   * the grilles builder — fractions are accepted in both units there, so they
   * are here too, even though they only really make sense in inches.
   *
   * @param {string} str - The raw input value
   * @returns {number} The parsed measurement, or NaN if unparseable
   */

  parseDimensionValue(str) {
    if (!this.config.fraction_input) return Number.parseFloat(str);
    const s = String(str).trim();
    const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixed)
      return parseInt(mixed[1]) + parseInt(mixed[2]) / parseInt(mixed[3]);
    const fraction = s.match(/^(\d+)\/(\d+)$/);
    if (fraction) return parseInt(fraction[1]) / parseInt(fraction[2]);
    if (/^\d+(\.\d+)?$/.test(s)) return Number.parseFloat(s);
    return NaN;
  }

  /**
   * Get the rail length in millimeters,
   * converting from inches if necessary.
   * Returns null if the input is invalid.
   * @returns {number|null} The rail length in millimeters,
   *                        or null if invalid
   */

  lengthInMm() {
    const raw = this.parseDimensionValue(this.value("rail_length"));
    if (!Number.isFinite(raw) || raw <= 0) return null;
    const unit = this.radio("unit") || this.config.unit || "mm";
    return unit === "in" ? raw * 25.4 : raw;
  }

  /**
   * Get the through post count for the current rail length.
   * Returns null if the length is invalid or exceeds the maximum allowed, so
   * callers can tell "no answer yet" from a legitimate count of 0.
   * @returns {number|null} The through post count, or null if invalid
   */

  throughPosts() {
    const lengthMm = this.lengthInMm();
    if (lengthMm === null || lengthMm > MAX_RAIL_LENGTH_MM) return null;
    return throughPostsForLength(lengthMm);
  }

  /**
   * Validate the rail length locally: readable, positive, and within range.
   *
   * The range check has to happen here rather than being left to the API, because
   * the API returns only ONE error per response. Once another field is also
   * invalid — switch the unit to inches and the return lengths instantly are —
   * every response is about that other field, and the API never mentions rail
   * length again. forgetErrorsForInput() drops the remembered rail-length error
   * the moment the user edits the field, so without a local check the field goes
   * silent whatever is typed into it. Validating here also spares a round-trip
   * for a length we already know is out of range.
   *
   * The limit is phrased from MAX_RAIL_LENGTH_MM in the unit on screen, so the
   * message tracks that constant rather than the API's copy of it.
   *
   * Blank is not an error — the field is simply unfinished.
   *
   * @returns {{field: string, message: string, enquiry?: boolean}|null} The
   *          offending field and a message to show under it, or null when the
   *          length is usable. `enquiry` marks the over-length case, which is an
   *          invitation to enquire rather than a mistake.
   */

  getLengthValidationError() {
    const raw = this.value("rail_length");
    if (!raw) return null;

    const parsed = this.parseDimensionValue(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return {
        field: "rail_length",
        message: "Please enter a valid measurement, e.g. 2 or 2 1/2.",
      };
    }

    const lengthMm = this.lengthInMm();
    if (lengthMm !== null && lengthMm > MAX_RAIL_LENGTH_MM) {
      const unit = this.radio("unit") || this.config.unit || "mm";
      const limit =
        unit === "in"
          ? `${(MAX_RAIL_LENGTH_MM / 25.4).toFixed(2)} in`
          : `${MAX_RAIL_LENGTH_MM} mm`;
      return {
        field: "rail_length",
        enquiry: true,
        message: `To specify a rail length exceeding ${limit}, please complete our enquiry form.`,
      };
    }

    return null;
  }

  /**
   * Check the configuration is complete enough to price.
   *
   * A return with no length is not "no return" — it is an unfinished choice.
   * getParams() omits a blank return length, so without this guard the API is
   * never told about the return, prices a 0CP rail, and the user's selection is
   * silently ignored. It also lets termination combos the UI blocks reach the
   * API anyway, since updateTerminationOptions() reads the form while the SKU
   * follows what we send. Mirrors getDimensionValidationError() in the grilles
   * builder, which refuses to price an incomplete grille.
   *
   * @returns {{field: string, message: string}|null} The offending field and a
   *          message to show under it, or null when safe to price
   */

  getReturnValidationError() {
    const returns = this.radio("returns") || "none";
    const sides = [];
    if (returns === "left" || returns === "both") {
      sides.push(["left_return_length", "left"]);
    }
    if (returns === "right" || returns === "both") {
      sides.push(["right_return_length", "right"]);
    }

    for (const [field, side] of sides) {
      const length = this.parseDimensionValue(this.value(field));
      if (!Number.isFinite(length) || length <= 0) {
        return {
          field,
          message: `Enter a ${side} return length between 50mm and 500mm.`,
        };
      }
    }

    return null;
  }

  /**
   * Get the parameters to send to the pricing API based on the current form state.
   * @returns {object} The parameters for the pricing API
   */

  getParams() {
    const termination = this.radio("termination") || "end_posts";

    const params = {
      rail_size: this.radio("rail_size") || "small",
      unit: this.radio("unit") || this.config.unit || "mm",
      rail_length: this.parseDimensionValue(this.value("rail_length")) || 0,
      end_posts:
        termination === "end_posts" ? 2 : termination === "mixed" ? 1 : 0,
      wall_brackets:
        termination === "wall_brackets" ? 2 : termination === "mixed" ? 1 : 0,
      through_posts: this.throughPosts() ?? 0,
      finish: this.radio("finish") || DEFAULT_FINISH,
      currency: this.config.currency || "GBP",
    };

    // Capture shelf material (ignored by API but needs capturing for order)
    const shelfMaterial = this.radio("shelf_material");
    if (shelfMaterial) params.shelf_material = shelfMaterial;

    // Include a side's length only when that side is selected and filled
    const returns = this.radio("returns") || "none";
    if (returns === "left" || returns === "both") {
      const left = this.parseDimensionValue(this.value("left_return_length"));
      if (left > 0) params.left_return_length = left;
    }
    if (returns === "right" || returns === "both") {
      const right = this.parseDimensionValue(this.value("right_return_length"));
      if (right > 0) params.right_return_length = right;
    }

    // Only include shopify/company_location_id if they are non-zero
    if (this.config.shopify_id != 0) {
      params.shopify_id = this.config.shopify_id;
    }
    if (this.config.company_location_id != 0) {
      params.company_location_id = this.config.company_location_id;
    }

    return params;
  }

  /* -------------------------------------------------------------------------- */
  /*                               Change handling                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Watch for changes to the form inputs and trigger a resolve() when they change.
   * Radio/select inputs resolve instantly, text inputs resolve after a delay.
   */

  watchForChanges() {
    // Radio/select inputs resolve instantly
    this.addEventListener("change", (e) => {
      // Don't do anything for these inputs
      if (e.target.name === "shelf_material") return;

      if (e.target.name === "returns") {
        this.updateReturnFields();
        this.updateTerminationOptions();
      }
      // Give the edited field a fresh chance before re-pricing, and drop its
      // stale error from the UI immediately.
      this.forgetErrorsForInput(e.target.name);
      this.renderErrors();
      this.resolve();
    });

    // Text inputs resolve after a delay
    this.addEventListener("input", (e) => {
      if (e.target.matches('input[type="text"], input[type="number"]')) {
        this.forgetErrorsForInput(e.target.name);
        this.renderErrors();
        this.scheduleResolve();
      }
    });
  }

  /**
   * Drop the remembered errors an edit to the named input could fix, so the
   * field re-validates cleanly on the next resolve(). Field errors come from
   * FORGET_ON_EDIT; the "finish" input clears the global info-box instead,
   * since unsupported-finish errors have no field.
   * @param {string} name - The `name` of the edited input
   */

  forgetErrorsForInput(name) {
    if (name === "finish") {
      this.globalError = null;
      return;
    }
    const keys = FORGET_ON_EDIT[name];
    if (!keys) return;
    keys.forEach((key) => this.fieldErrors.delete(key));
  }

  /**
   * Schedule a resolve() to run after a short delay, cancelling any previous
   * scheduled resolve. This is used to debounce rapid input changes.
   */

  scheduleResolve() {
    clearTimeout(this._debounce);
    this._debounce = setTimeout(() => this.resolve(), 500);
  }

  /**
   * Update the visibility of the left and right return length fields based on the
   * selected returns option.
   *
   * If "left" or "both" is selected, the left return field is shown.
   * If "right" or "both" is selected, the right return field is shown.
   *
   * Otherwise, both fields are hidden.
   */

  updateReturnFields() {
    const returns = this.radio("returns") || "none";
    const left = this.querySelector('[data-return="left"]');
    const right = this.querySelector('[data-return="right"]');
    if (left) left.hidden = !(returns === "left" || returns === "both");
    if (right) right.hidden = !(returns === "right" || returns === "both");
  }

  /**
   * Enable/disable termination options to match the renders that exist.
   *
   * From the CDN render set: "1 end post + 1 wall bracket" exists only with
   * exactly one return (CPL/CPR). "2 wall brackets" exists with every return
   * option, so it is never disabled.
   */

  updateTerminationOptions() {
    const returns = this.radio("returns") || "none";
    const mixedInput = this.querySelector(
      'input[name="termination"][value="mixed"]',
    );
    const mixedTooltip =
      mixedInput?.parentElement?.querySelector("info-tooltip");

    const allowMixed = returns === "left" || returns === "right";

    if (mixedInput) mixedInput.disabled = !allowMixed;
    if (mixedTooltip)
      mixedTooltip.style.display = allowMixed ? "none" : "block";

    // Fall back to end posts if the current selection is no longer valid
    if (this.radio("termination") === "mixed" && !allowMixed) {
      const epInput = this.querySelector(
        'input[name="termination"][value="end_posts"]',
      );
      if (epInput) epInput.checked = true;
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Pricing                                  */
  /* -------------------------------------------------------------------------- */

  /**
   * Whether the visitor is in a market we do not sell to. geolocation.js adds
   * the class to <body> once its country lookup lands, and fires
   * `geo:restricted` at the same moment for anything already rendered.
   * @returns {boolean}
   */

  isGeoRestricted() {
    return document.body.classList.contains("geo-restricted");
  }

  /**
   * Build the SKU for the current configuration locally.
   * Format: COT/SHL/RAIL/{rail_size}/{through_posts}TP/{returns}/{end_posts}EP/{wall_brackets}WB/{finish}
   * @returns {string} The formatted SKU
   */

  buildProductSku() {
    const size = this.radio("rail_size") === "large" ? "LRG" : "SML";
    const tp = `${this.throughPosts() ?? 0}TP`;

    const returns = this.radio("returns") || "none";
    let cp = "0CP";
    if (returns === "left") cp = "CPL";
    else if (returns === "right") cp = "CPR";
    else if (returns === "both") cp = "2CP";

    const termination = this.radio("termination") || "end_posts";
    let epWb = "2EP/0WB";
    if (termination === "mixed") epWb = "1EP/1WB";
    else if (termination === "wall_brackets") epWb = "0EP/2WB";

    const finish = this.radio("finish") || DEFAULT_FINISH;

    return `COT/SHL/RAIL/${size}/${tp}/${cp}/${epWb}/${finish}`;
  }

  /**
   * Drop a pricing failure without surfacing it. The pricing API carries no
   * prices for restricted markets — Canada asks for CAD, which it does not
   * have — so every request 404s there, and the message means nothing to a
   * visitor who has no price and no Add to basket to begin with. The
   * configuration is still unpriceable, so reset the price and the button the
   * same way a shown error would.
   */

  discardPricingError() {
    this.lastEnquiryReason = null;
    this.clearError();
    this.markUnpriceable();
  }

  /**
   * Resolve the current form state to get a price from the API.
   * This method fetches the price from the API and updates the UI accordingly.
   * It also handles stale requests by ignoring responses from previous requests
   * by using a request ID counter.
   */

  async resolve() {
    this.setThroughPostsDisplay(this.throughPosts());

    this.product.sku = this.buildProductSku();
    if (this.skuEl) this.skuEl.textContent = this.product.sku;
    this.updateGalleryLayers();

    // Never price a length we already know the API will refuse. Over-length is
    // an enquiry trigger, so carry the reason across for the enquiry form the
    // same way a RAIL_LENGTH_ENQUIRY response would.
    const lengthError = this.getLengthValidationError();
    if (lengthError) {
      this.lastEnquiryReason = lengthError.enquiry ? lengthError.message : null;
      this.showFieldError(lengthError.field, lengthError.message);
      this.markUnpriceable();
      return;
    }

    // Never price a configuration the user has not finished making
    const incomplete = this.getReturnValidationError();
    if (incomplete) {
      this.showFieldError(incomplete.field, incomplete.message);
      this.markUnpriceable();
      return;
    }

    // Nothing on the page needs this request when the visitor is restricted:
    // the price and Add to basket are stripped out by geolocation.js, the SKU
    // and the preview layers are built locally, and the pricing API carries no
    // prices for their market anyway, so it would only ever return an error.
    if (this.isGeoRestricted()) {
      this.discardPricingError();
      return;
    }

    const query = new URLSearchParams(this.getParams());
    const requestId = ++this.lastRequestId;

    this.setPriceText("Calculating price...");

    try {
      const res = await fetch(`${this.API_URL}?${query}`);
      const data = await res.json();

      // Only update the UI if this is the latest request
      if (requestId !== this.lastRequestId) {
        console.warn("[gallery-rail] ignoring stale request", requestId);
        return;
      }

      if (res.ok) {
        // 200 OK, we have a price
        this.applyResult(data);
      } else {
        this.lastEnquiryReason = ENQUIRY_ERRORS.includes(data.error_name)
          ? data.error_message
          : null;
        const message =
          data.error_message || "Unable to price this configuration.";
        const fieldKey = FIELD_ERROR_MAP[data.error_name];
        if (fieldKey) {
          this.showFieldError(fieldKey, message);
        } else {
          this.showError(message);
        }
        this.markUnpriceable();
      }
    } catch (err) {
      if (requestId !== this.lastRequestId) {
        console.warn("[gallery-rail] ignoring stale request", requestId);
        return;
      }
      console.error("[gallery-rail] error fetching price", err);
      this.showError("Unable to price this configuration.");
      this.markUnpriceable();
    }
  }

  /**
   * Apply the pricing API result to the product state and update the UI.
   * @param {object} data - The pricing API response data
   */

  applyResult(data) {
    this.product.sku = data.sku || this.product.sku;
    this.product.price = Math.round(data.sale_price * 100); // Convert to pence/cents
    this.product.rrp = Math.round(data.rrp * 100); // Convert to pence/cents
    this.lastEnquiryReason = null;

    if (this.skuEl) this.skuEl.textContent = this.product.sku;
    this.clearError();

    // A restricted visitor still gets the SKU and the preview render, but never
    // a price or a way to buy — geolocation.js strips those controls out.
    if (this.isGeoRestricted()) return;

    this.hasValidPrice = true;
    if (this.addToCartButton) this.addToCartButton.disabled = false;
    this.displayPrice();
  }

  /**
   * Display the formatted price, plus the RRP for B2B customers.
   */

  displayPrice() {
    if (!this.priceEls?.length) {
      console.warn("[gallery-rail] no price element to display price in");
      return;
    }

    const { tax, country, currency } = this.config;
    const formatted = window.AM.formatCurrency(
      this.product.price,
      tax,
      country,
      currency,
    );

    this.priceEls.forEach((el) => {
      el.innerHTML = formatted;
    });

    this.displayRrp(this.product.rrp);
  }

  /**
   * Write the list price (RRP) alongside the trade price. Only B2B customers get
   * the RRP markup, and the wrapper stays hidden until we have a real RRP to show.
   * @param {number|null} rrp - The RRP in minor units, or null to hide it
   */

  displayRrp(rrp) {
    if (!this.rrpEls?.length) return;

    const show = Number.isFinite(rrp) && rrp > 0;
    const { tax, country, currency } = this.config;

    this.rrpEls.forEach((el) => {
      el.innerHTML = show
        ? window.AM.formatCurrency(rrp, tax, country, currency)
        : "";

      const wrapper = el.closest(".price-rrp-wrapper");
      if (wrapper) wrapper.style.display = show ? "" : "none";
    });
  }

  /**
   * Set plain text (a placeholder or status message) in the price elements.
   * @param {string} text - The text to display
   */

  setPriceText(text) {
    this.priceEls?.forEach((el) => {
      el.textContent = text;
    });
  }

  /**
   * Update the through posts display. Shows the count for a valid rail length,
   * or a dash ("–") when the length is missing or out of range.
   * @param {number|null} count - The through post count, or null if invalid
   */

  setThroughPostsDisplay(count) {
    if (this.throughPostsEl) {
      this.throughPostsEl.textContent = count === null ? "–" : count;
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Gallery                                  */
  /* -------------------------------------------------------------------------- */

  /**
   * Build the preview layer stack for the current configuration.
   *
   * The filename is the returned SKU alone (slashes → underscores). Renders no
   * longer carry a length band suffix (Aug 2026): the client re-cut them so each
   * through post count is one render, and the count is already in the SKU as
   * `nTP`, so the suffix was duplicate information.
   *
   * Only the rail layer exists on the CDN today. The shelf layer will slot in
   * beneath it (lower zIndex) once the client supplies the renders and their
   * naming — shelf_material is in neither the SKU nor any filename, so it has
   * to render outside the rail image.
   *
   * @returns {Array<{url: string, zIndex: number, alt: string}>}
   */

  buildImageLayers() {
    const sku = this.product.sku || this.buildProductSku();
    if (!sku) return [];

    const filename = `GALLERYRAIL_${sku.replace(/\//g, "_")}`;

    return [
      {
        url: `${this.IMAGE_BASE_URL}/${filename}.webp?${this.IMAGE_PARAMS}`,
        zIndex: 1,
        alt: `Gallery rail SKU ${sku}`,
      },
    ];
  }

  /**
   * Render the layer stack. A layer that 404s is dropped; the placeholder
   * returns only if every layer fails. A missing render is not an error —
   * price and Add to Cart are deliberately left alone.
   */

  updateGalleryLayers() {
    if (!this.layersContainer) return;

    const layers = this.buildImageLayers();
    this.layersContainer.innerHTML = "";

    if (!layers.length) {
      this.setLoading(false);
      this.showPlaceholder(true);
      return;
    }

    this.setLoading(true);

    let settledCount = 0;
    let successCount = 0;

    const checkDone = () => {
      settledCount++;
      if (settledCount >= layers.length) {
        this.setLoading(false);
        this.showPlaceholder(successCount === 0);
      }
    };

    layers.forEach((layer) => {
      const img = document.createElement("img");
      img.src = layer.url;
      img.alt = layer.alt;
      img.width = 1024;
      img.height = 1024;
      img.className =
        "gallery-rail__gallery__layer gallery-rail__gallery__layer--loading";
      img.style.zIndex = layer.zIndex;
      img.onload = () => {
        successCount++;
        img.classList.remove("gallery-rail__gallery__layer--loading");
        checkDone();
      };
      img.onerror = () => {
        img.remove();
        checkDone();
      };
      this.layersContainer.appendChild(img);
    });
  }

  /**
   * Toggle between the layer stack and the "Select options to preview" placeholder.
   * @param {boolean} show - true to show the placeholder and hide the layers
   */

  showPlaceholder(show) {
    if (this.placeholderEl) this.placeholderEl.hidden = !show;
    if (this.layersContainer) this.layersContainer.hidden = show;
  }

  /**
   * Toggle the loading spinner shown over the gallery while a preview image
   * is being fetched.
   * @param {boolean} show - true to show the spinner, false to hide it
   */

  setLoading(show) {
    if (this.loadingEl) this.loadingEl.hidden = !show;
  }

  /* -------------------------------------------------------------------------- */
  /*                               Error handling                               */
  /* -------------------------------------------------------------------------- */

  /**
   * Shared side-effects for any error state: no valid price, Add to basket
   * disabled, price cleared.
   */

  markUnpriceable() {
    this.hasValidPrice = false;
    if (this.addToCartButton) this.addToCartButton.disabled = true;
    this.setPriceText("—");
    this.displayRrp(null);
    this.setLoading(false);
    this.showPlaceholder(true);

    // The SKU goes too. applyResult() is its only writer, so once a
    // configuration stops pricing there is no SKU that describes it — and
    // keeping the last one is worse than having none, because
    // buildSpecEntries() would send it to the enquiry form alongside the new
    // measurements ("Rail length: 2800mm, SKU: …_1200_…"). A restricted
    // market is unaffected: applyResult() returns before anything calls this,
    // so the SKU that names the preview render survives.
    this.product.sku = "";
    this.product.price = null;
    this.product.rrp = null;
    if (this.skuEl) this.skuEl.textContent = "";
  }

  /**
   * Record a global info-box error, for errors that do not map to a specific
   * field, then re-render. Coexists with any remembered field errors — the two
   * no longer wipe each other.
   * @param {string} message - The error message to display
   */

  showError(message) {
    this.globalError = message;
    this.renderErrors();
  }

  /**
   * Record an error against the field it belongs to, then re-render. The error
   * persists until the user edits that field or a valid price clears it, and
   * coexists with any other remembered field or global error.
   * @param {string} fieldKey - A FIELD_ERROR_MAP value or a return field name
   * @param {string} message - The error message to display
   */

  showFieldError(fieldKey, message) {
    this.fieldErrors.set(fieldKey, message);
    this.renderErrors();
  }

  /**
   * Render every remembered error from scratch. Wipes the current error DOM
   * (inline field divs and the info-box), then re-renders each remembered field
   * error into its target(s) plus the global info-box error if set. Marks the
   * configuration unpriceable whenever any error remains.
   */

  renderErrors() {
    this.clearGlobalError();
    this.clearFieldErrors();

    const hasErrors = this.fieldErrors.size > 0 || this.globalError !== null;

    this.fieldErrors.forEach((message, fieldKey) => {
      this.fieldErrorTargets(fieldKey).forEach((container) => {
        this.renderFieldError(container, message);
      });
    });

    if (this.globalError !== null && this.errorEl) {
      this.errorEl.parentElement.style.display = "flex";
      this.errorEl.textContent = this.globalError;
    }

    if (hasErrors) this.markUnpriceable();
  }

  /**
   * Render one error inside a field container: flag the container so its
   * control picks up the red outline, append the message underneath, and point
   * the field's inputs at it for screen readers. Every error target carries a
   * data-field, so that is what the message id is keyed on.
   * @param {HTMLElement} container - A [data-field] wrapper
   * @param {string} message - The error message to display
   */

  renderFieldError(container, message) {
    container.classList.add("builder-form__field--error");

    const errorId = `builder-form__error--${container.dataset.field}`;

    const div = document.createElement("div");
    div.className = "builder-form__field-error";
    div.id = errorId;
    div.setAttribute("role", "alert");
    div.innerHTML = ERROR_ICON;

    const text = document.createElement("span");
    text.textContent = message;
    div.appendChild(text);
    container.appendChild(div);

    container.querySelectorAll("input").forEach((input) => {
      input.setAttribute("aria-invalid", "true");
      input.setAttribute("aria-describedby", errorId);
    });
  }

  /**
   * Resolve a field key to the container element(s) an error should render in.
   * The "returns" key maps to every visible return sub-field, since the API
   * does not say which side is out of range. All other keys map to the single
   * [data-field] wrapper, and are skipped if hidden.
   * @param {string} fieldKey
   * @returns {HTMLElement[]}
   */

  fieldErrorTargets(fieldKey) {
    if (fieldKey === "returns") {
      return Array.from(
        this.querySelectorAll(".gallery-rail__return-length"),
      ).filter((el) => !el.hidden);
    }
    return Array.from(
      this.querySelectorAll(`[data-field="${fieldKey}"]`),
    ).filter((el) => !el.hidden);
  }

  /**
   * Remove every inline field error currently rendered, along with the outline
   * flag and the aria wiring renderFieldError() added.
   */

  clearFieldErrors() {
    this.querySelectorAll(".builder-form__field--error").forEach((el) =>
      el.classList.remove("builder-form__field--error"),
    );
    this.querySelectorAll("[aria-invalid]").forEach((el) => {
      el.removeAttribute("aria-invalid");
      el.removeAttribute("aria-describedby");
    });
    this.querySelectorAll(".builder-form__field-error").forEach((el) =>
      el.remove(),
    );
  }

  /**
   * Hide and empty the global info-box error.
   */

  clearGlobalError() {
    if (this.errorEl) {
      this.errorEl.parentElement.style.display = "none";
      this.errorEl.textContent = "";
    }
  }

  /**
   * Clear all error states — remembered field errors and the global error — and
   * re-render so the UI empties. Called when a valid price is applied.
   */

  clearError() {
    this.fieldErrors.clear();
    this.globalError = null;
    this.renderErrors();
  }

  /* -------------------------------------------------------------------------- */
  /*                              Enquiry handling                              */
  /* -------------------------------------------------------------------------- */

  buildSpecEntries() {
    const unit = this.radio("unit") || this.config.unit || "mm";
    const returns = this.radio("returns") || "none";
    const termination = this.radio("termination") || "end_posts";
    const throughPosts = this.throughPosts();

    const entries = [
      { label: "Rail size", value: this.radio("rail_size") || "small" },
      { label: "Unit", value: unit === "in" ? "Inches" : "Millimeters" },
      { label: "Rail length", value: `${this.value("rail_length")}${unit}` },
      {
        label: "Through posts",
        value: throughPosts === null ? "–" : String(throughPosts),
      },
      {
        label: "Configuration",
        value:
          termination === "end_posts"
            ? "2 End posts"
            : termination === "mixed"
              ? "1 End post + 1 Wall bracket"
              : "2 Wall brackets",
      },
      { label: "Returns", value: returns === "none" ? "None" : returns },
    ];

    if (returns === "left" || returns === "both") {
      entries.push({
        label: "Left return length",
        value: `${this.value("left_return_length")}${unit}`,
      });
    }
    if (returns === "right" || returns === "both") {
      entries.push({
        label: "Right return length",
        value: `${this.value("right_return_length")}${unit}`,
      });
    }

    entries.push({
      label: "Shelf material",
      value: this.radio("shelf_material") || "–",
    });
    entries.push({ label: "Finish", value: this.radio("finish") || "–" });
    if (this.product.sku)
      entries.push({ label: "SKU", value: this.product.sku });
    if (this.lastEnquiryReason)
      entries.push({ label: "Enquiry reason", value: this.lastEnquiryReason });

    return entries;
  }

  async handleAddToCart() {
    if (!this.hasValidPrice) return;

    const button = this.addToCartButton;
    if (button) button.disabled = true;

    try {
      const termination = this.radio("termination") || "end_posts";
      const creatorPayload = {
        product_type: "gallery_rail",
        rail_size: this.radio("rail_size") || "small",
        rail_length: this.parseDimensionValue(this.value("rail_length")) || 0,
        unit: this.radio("unit") || this.config.unit || "mm",
        end_posts: termination === "end_posts" ? 2 : 0,
        through_posts: this.throughPosts() ?? 0,
        wall_brackets: termination === "wall_brackets" ? 2 : 0,
        finish: this.radio("finish") || DEFAULT_FINISH,
        title: this.config.product_title || "Custom Gallery Rail",
        currency: this.config.currency,
      };

      const returns = this.radio("returns") || "none";
      if (returns === "left" || returns === "both") {
        const left = this.parseDimensionValue(this.value("left_return_length"));
        if (left > 0) creatorPayload.left_return_length = left;
      }
      if (returns === "right" || returns === "both") {
        const right = this.parseDimensionValue(
          this.value("right_return_length"),
        );
        if (right > 0) creatorPayload.right_return_length = right;
      }
      const shelfMaterial = this.radio("shelf_material");
      if (shelfMaterial) creatorPayload.shelf_material = shelfMaterial;

      if (this.config.shopify_id != 0) {
        creatorPayload.shopify_id = this.config.shopify_id;
      }
      if (this.config.company_location_id != 0) {
        creatorPayload.company_location_id = this.config.company_location_id;
      }

      const skipLabels = new Set(["SKU", "Enquiry reason"]);
      const properties = {};
      this.buildSpecEntries()
        .filter(({ label }) => !skipLabels.has(label))
        .forEach(({ label, value }) => {
          properties[label] = value;
        });

      properties["Custom Product Raw"] = JSON.stringify({
        ...this.config,
        sku: this.product.sku,
        price: this.product.price,
        rrp: this.product.rrp,
      });

      console.log("[gallery-rail] adding bespoke product to cart", {
        creatorPayload,
        properties,
      });

      await addBespokeProductToCart({
        creatorPayload,
        properties,
        quantity: this.quantity(),
      });

      const addSpan = button?.querySelector(".gallery-rail__atc--add");
      const addedSpan = button?.querySelector(".gallery-rail__atc--added");
      if (addSpan) addSpan.hidden = true;
      if (addedSpan) addedSpan.hidden = false;

      setTimeout(() => {
        if (addSpan) addSpan.hidden = false;
        if (addedSpan) addedSpan.hidden = true;
        if (button) button.disabled = false;
      }, 2000);
    } catch (err) {
      this.showError(err.message || "Failed to add to cart.");
      if (button) button.disabled = false;
    }
  }

  openEnquiry() {
    if (!this.enquiryForm) return;
    const entries = this.buildSpecEntries();

    // Human-readable summary in the modal
    const summary = this.enquiryForm.querySelector("[data-spec-summary]");
    if (summary) {
      const dl = document.createElement("dl");
      dl.className = "modal__product-builder-enquiry__spec-list";
      entries.forEach(({ label, value }) => {
        const div = document.createElement("div");
        const dt = document.createElement("dt");
        dt.textContent = label;
        const dd = document.createElement("dd");
        dd.textContent = value;
        div.append(dt, dd);
        dl.appendChild(div);
      });
      summary.innerHTML = "";
      summary.appendChild(dl);
    }

    // Hidden inputs so the spec is submitted with the contact form
    const fields = this.enquiryForm.querySelector("[data-spec-fields]");
    if (fields) {
      fields.innerHTML = "";
      entries.forEach(({ label, value }) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `contact[${label}]`;
        input.value = value;
        fields.appendChild(input);
      });
    }

    const dialog = this.enquiryForm.querySelector("dialog");
    if (dialog && !dialog.open) this.enquiryForm.open();
  }
}

customElements.define("gallery-rail-builder", GalleryRailBuilderForm);
